// Dynamic field editor for a template's `fields_json` (docs/09 §B1). Renders one
// input per field, typed by `field.type`: text, multiline, color swatch, or an
// image slot that reuses the shared MediaPickerModal. Shared by the "create from
// template" form and the media detail drawer's edit surface, so editing a live
// slide and instantiating a new one use the exact same control + validation.

import { useState } from 'react';
import { MediaPickerModal } from '../../components/MediaPickerModal.tsx';
import { MediaThumb } from '../../components/MediaThumb.tsx';
import { Button } from '../../components/ui.tsx';
import { useMedia } from '../../api/media.ts';
import type { TemplateField, TemplateFields } from '../../api/templates.ts';

/** Per-field validation: only max_len is enforced (the backend is authoritative). */
export function validateFields(
  fields: TemplateField[],
  values: TemplateFields,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const f of fields) {
    const val = values[f.key] ?? '';
    if (f.max_len != null && val.length > f.max_len) {
      errors[f.key] = `Too long — ${val.length}/${f.max_len} characters`;
    }
  }
  return errors;
}

function ImageField({
  field,
  value,
  onChange,
}: {
  field: TemplateField;
  value: string;
  onChange: (v: string) => void;
}) {
  const [picking, setPicking] = useState(false);
  const media = useMedia();
  const picked = value ? media.data?.find((m) => m.id === value) : undefined;
  return (
    <div className="tpl-image-field">
      {picked ? (
        <MediaThumb media={picked} className="tpl-image-thumb" />
      ) : (
        <div className="tpl-image-thumb tpl-image-empty">
          <svg
            width={24}
            height={24}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <circle cx="8.5" cy="10" r="1.5" />
            <path d="M21 15.5l-5-5-8 8" />
          </svg>
        </div>
      )}
      <div className="row">
        <Button size="sm" type="button" onClick={() => setPicking(true)}>
          {value ? 'Change image' : 'Choose image'}
        </Button>
        {value && (
          <Button size="sm" variant="ghost" type="button" onClick={() => onChange('')}>
            Clear
          </Button>
        )}
      </div>
      {picking && (
        <MediaPickerModal
          title={`Choose ${field.label.toLowerCase()}`}
          onPick={(id) => {
            onChange(id);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}

export function TemplateFieldEditor({
  fields,
  values,
  onChange,
}: {
  fields: TemplateField[];
  values: TemplateFields;
  onChange: (values: TemplateFields) => void;
}) {
  const errors = validateFields(fields, values);
  const set = (key: string, v: string) => onChange({ ...values, [key]: v });

  return (
    <div className="tpl-fields">
      {fields.map((f) => {
        const value = values[f.key] ?? '';
        const err = errors[f.key];
        const counter = f.max_len != null ? `${value.length}/${f.max_len}` : null;
        return (
          <div className="field" key={f.key}>
            <label htmlFor={`tf-${f.key}`}>
              {f.label}
              {counter && (
                <span className={`tpl-counter${err ? ' over' : ''}`}> {counter}</span>
              )}
            </label>

            {f.type === 'multiline' ? (
              <textarea
                id={`tf-${f.key}`}
                rows={4}
                value={value}
                aria-invalid={err ? 'true' : undefined}
                onChange={(e) => set(f.key, e.target.value)}
              />
            ) : f.type === 'color' ? (
              <div className="tpl-color-row">
                <input
                  type="color"
                  aria-label={`${f.label} color`}
                  value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'}
                  onChange={(e) => set(f.key, e.target.value)}
                />
                <input
                  id={`tf-${f.key}`}
                  className="mono"
                  value={value}
                  spellCheck={false}
                  onChange={(e) => set(f.key, e.target.value)}
                />
              </div>
            ) : f.type === 'image_media_id' ? (
              <ImageField field={f} value={value} onChange={(v) => set(f.key, v)} />
            ) : (
              <input
                id={`tf-${f.key}`}
                value={value}
                aria-invalid={err ? 'true' : undefined}
                onChange={(e) => set(f.key, e.target.value)}
              />
            )}

            {err && <span className="error-text">{err}</span>}
          </div>
        );
      })}
    </div>
  );
}
